import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Autocomplete,
    Alert,
    CircularProgress,
    Chip,
    Paper,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaceIcon from '@mui/icons-material/Place';
import { LpsInstallation, InstallationStatus, LpsDevice } from '../lps.types';
import {
    createInstallation,
    updateInstallation,
    getInstallation,
    getDevices,
    getDevice,
    getInstallations,
    associateInstallationDevices,
    dissociateInstallationDevices,
} from '../lps.api';
import { Organization } from 'utils';
import { adminGetAllOrganizations, adminViewOrganization } from '../../../admin.organizations.api';
import { debounce } from 'lodash';

interface Props {
    open: boolean;
    installation: LpsInstallation | null;
    onClose: () => void;
    onSuccess: () => void;
}

const STATUS_OPTIONS: InstallationStatus[] = ['planning', 'active', 'inactive', 'decommissioned'];
const STATUS_COLORS: Record<InstallationStatus, 'info' | 'success' | 'default' | 'error'> = {
    planning: 'info',
    active: 'success',
    inactive: 'default',
    decommissioned: 'error',
};

const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Gateways tied to another Active installation are hidden unless we are editing that installation. */
function filterGatewaysForInstallationPicker(
    gateways: LpsDevice[],
    installations: LpsInstallation[],
    editingInstallationUuid: string | null,
): LpsDevice[] {
    return gateways.filter((g) => {
        const activeOwner = installations.find(
            (i) => i.status === 'active' && i.gateway_uuid === g.uuid,
        );
        if (!activeOwner) {
            return true;
        }
        return editingInstallationUuid != null && activeOwner.uuid === editingInstallationUuid;
    });
}

async function fetchAllInstallationsForGatewayPicker(): Promise<LpsInstallation[]> {
    const merged: LpsInstallation[] = [];
    let page = 1;
    let lastPage = 1;
    do {
        const res = await getInstallations({ page, per_page: 50 });
        merged.push(...((res.data?.data ?? []) as LpsInstallation[]));
        lastPage = res.data?.meta?.last_page ?? 1;
        page++;
    } while (page <= lastPage);
    return merged;
}

type DialogStep = 'details' | 'deviceAssignments';

const LpsInstallationDialog: React.FC<Props> = ({ open, installation, onClose, onSuccess }) => {
    const isEdit = !!installation;
    const [dialogStep, setDialogStep] = useState<DialogStep>('details');

    const [name, setName] = useState('');
    const [locationAddress, setLocationAddress] = useState('');
    const [locationNotes, setLocationNotes] = useState('');
    const [status, setStatus] = useState<InstallationStatus>('planning');
    const [iotThingName, setIotThingName] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [installedAt, setInstalledAt] = useState('');

    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

    const [availableGateways, setAvailableGateways] = useState<LpsDevice[]>([]);
    const [selectedGateway, setSelectedGateway] = useState<LpsDevice | null>(null);

    const [installationDevices, setInstallationDevices] = useState<LpsDevice[]>([]);
    const [allTrackableDevices, setAllTrackableDevices] = useState<LpsDevice[]>([]);
    const [selectedDevicesToAssociate, setSelectedDevicesToAssociate] = useState<LpsDevice[]>([]);
    const [selectedDevicesToDissociate, setSelectedDevicesToDissociate] = useState<LpsDevice[]>([]);
    const [deviceActionNotes, setDeviceActionNotes] = useState('');
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [savingDeviceAssignments, setSavingDeviceAssignments] = useState(false);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [defectiveAssociateConfirmOpen, setDefectiveAssociateConfirmOpen] = useState(false);

    const fetchOrganizations = debounce((searchTerm: string) => {
        adminGetAllOrganizations(searchTerm, 500)
            .then((res) => setOrganizations(res.data.data))
            .catch((err) => console.error('Failed to fetch organizations', err));
    }, 300);

    useEffect(() => {
        if (!open) {
            setDialogStep('details');
            setDefectiveAssociateConfirmOpen(false);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            setDialogStep('details');
            fetchOrganizations('');
            Promise.all([
                getDevices({ device_type: 'gateway', per_page: 500 }),
                fetchAllInstallationsForGatewayPicker(),
            ])
                .then(([gwRes, installations]) => {
                    const gateways = (gwRes.data?.data ?? []) as LpsDevice[];
                    const editingId = installation?.uuid ?? null;
                    setAvailableGateways(
                        filterGatewaysForInstallationPicker(gateways, installations, editingId),
                    );
                })
                .catch((err) => console.error('Failed to fetch gateways or installations', err));

            if (installation) {
                setName(installation.name);
                setLocationAddress(installation.location_address || '');
                setLocationNotes(installation.location_notes || '');
                setStatus(installation.status);
                setIotThingName(installation.iot_thing_name || '');
                setDeviceName(installation.device_name || '');
                setInstalledAt(
                    installation.installed_at ? installation.installed_at.split('T')[0] : '',
                );
                setSelectedOrg(null);

                // Load full detail with devices
                loadInstallationDetails(installation.uuid);
                loadTrackableDevices();
            } else {
                setName('');
                setLocationAddress('');
                setLocationNotes('');
                setStatus('planning');
                setIotThingName('');
                setDeviceName('');
                setInstalledAt('');
                setSelectedOrg(null);
                setSelectedGateway(null);
                setInstallationDevices([]);
                setAllTrackableDevices([]);
                setSelectedDevicesToAssociate([]);
                setSelectedDevicesToDissociate([]);
                setDeviceActionNotes('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, installation]);

    /** Load the exact org by UUID — the paginated admin org list often omits it. */
    useEffect(() => {
        if (!open || !installation?.organization_uuid) {
            return;
        }

        adminViewOrganization(installation.organization_uuid)
            .then((res) => {
                const org = res.data;
                setOrganizations((prev) =>
                    prev.some((o) => o.uuid === org.uuid) ? prev : [org, ...prev],
                );
                setSelectedOrg(org);
            })
            .catch((err) => console.error('Failed to load organization for installation', err));
    }, [open, installation?.organization_uuid, installation?.uuid]);

    const loadInstallationDetails = async (installationUuid: string) => {
        setLoadingDevices(true);
        try {
            const res = await getInstallation(installationUuid);
            const inst = (res.data?.data ?? res.data) as LpsInstallation;
            const devices = inst.devices || [];
            setInstallationDevices(devices);

            if (inst.gateway_uuid) {
                let gwDevice = devices.find((d: LpsDevice) => d.uuid === inst.gateway_uuid) || null;
                // Gateway is linked via installations.gateway_uuid, not devices.installation_uuid,
                // so it is usually absent from the installation "devices" collection.
                if (!gwDevice) {
                    try {
                        const gwRes = await getDevice(inst.gateway_uuid);
                        gwDevice = (gwRes.data?.data ?? gwRes.data) as LpsDevice;
                    } catch {
                        gwDevice = null;
                    }
                }
                setSelectedGateway(gwDevice);
                if (gwDevice) {
                    setAvailableGateways((prev) =>
                        prev.some((g) => g.uuid === gwDevice!.uuid) ? prev : [gwDevice!, ...prev],
                    );
                }
            } else {
                setSelectedGateway(null);
            }
        } catch (err) {
            console.error('Failed to load installation details', err);
        } finally {
            setLoadingDevices(false);
        }
    };

    const loadTrackableDevices = async () => {
        try {
            const [anchorsRes, trackersRes] = await Promise.all([
                getDevices({ device_type: 'anchor', per_page: 500 }),
                getDevices({ device_type: 'tracker', per_page: 500 }),
            ]);
            const anchors = (anchorsRes.data?.data ?? []) as LpsDevice[];
            const trackers = (trackersRes.data?.data ?? []) as LpsDevice[];
            setAllTrackableDevices([...anchors, ...trackers]);
        } catch (err) {
            console.error('Failed to load trackable devices', err);
        }
    };

    const anchors = installationDevices.filter((d) => d.device_type === 'anchor');
    const trackers = installationDevices.filter((d) => d.device_type === 'tracker');
    const assignedTrackableDevices = [...anchors, ...trackers];

    const DECOMMISSION_BLOCKED =
        'You cannot decommission this installation without first un-assigning all devices (anchors, trackers, and gateway).';

    const handleSubmit = async () => {
        if (!name.trim()) {
            setError('Name is required');
            return;
        }

        if (status === 'decommissioned') {
            const hasAnchorsOrTrackers = assignedTrackableDevices.length > 0;
            const hasGateway = Boolean(selectedGateway);
            if (hasAnchorsOrTrackers || hasGateway) {
                setError(DECOMMISSION_BLOCKED);
                return;
            }
        }

        setSaving(true);
        setError('');
        try {
            // Send null for cleared optional fields so the API persists clears (undefined keys are omitted).
            const data = {
                name,
                organization_uuid: selectedOrg?.uuid ?? null,
                location_address: locationAddress.trim() || null,
                location_notes: locationNotes.trim() || null,
                status,
                gateway_uuid: selectedGateway?.uuid ?? null,
                iot_thing_name: iotThingName.trim() || null,
                device_name: deviceName.trim() || null,
                installed_at: installedAt || null,
            };

            if (isEdit && installation) {
                await updateInstallation(installation.uuid, data);
            } else {
                await createInstallation(data);
            }
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save installation');
        } finally {
            setSaving(false);
        }
    };

    const assignedDeviceListSx = {
        maxHeight: 160,
        overflow: 'auto' as const,
        pr: 0.5,
    };

    const renderAssignedAnchorsTrackersBoxes = (showModifyButtons: boolean) => (
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Paper
                variant="outlined"
                sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Anchors
                        </Typography>
                        <Chip label={anchors.length} size="small" color="info" />
                    </Box>
                    {showModifyButtons && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setDialogStep('deviceAssignments')}
                        >
                            Modify
                        </Button>
                    )}
                </Box>
                <Box sx={assignedDeviceListSx}>
                    {anchors.length > 0 ? (
                        anchors.map((d) => (
                            <Typography
                                key={d.uuid}
                                variant="body2"
                                sx={{ fontFamily: 'monospace', py: 0.25 }}
                            >
                                {d.serial_number}
                            </Typography>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No anchors assigned
                        </Typography>
                    )}
                </Box>
            </Paper>

            <Paper
                variant="outlined"
                sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.04),
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Trackers
                        </Typography>
                        <Chip label={trackers.length} size="small" color="success" />
                    </Box>
                    {showModifyButtons && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setDialogStep('deviceAssignments')}
                        >
                            Modify
                        </Button>
                    )}
                </Box>
                <Box sx={assignedDeviceListSx}>
                    {trackers.length > 0 ? (
                        trackers.map((d) => (
                            <Typography
                                key={d.uuid}
                                variant="body2"
                                sx={{ fontFamily: 'monospace', py: 0.25 }}
                            >
                                {d.serial_number}
                            </Typography>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No trackers assigned
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Box>
    );
    const associatedDeviceUuids = new Set(assignedTrackableDevices.map((d) => d.uuid));
    const availableForAssociation = allTrackableDevices.filter(
        (d) => !associatedDeviceUuids.has(d.uuid),
    );

    const runAssociateDevices = async () => {
        if (!installation || selectedDevicesToAssociate.length === 0) return;

        setSavingDeviceAssignments(true);
        setError('');
        try {
            const response = await associateInstallationDevices(installation.uuid, {
                device_uuids: selectedDevicesToAssociate.map((d) => d.uuid),
                event_notes: deviceActionNotes || undefined,
            });

            const updatedInstallation = (response.data?.data ?? response.data) as LpsInstallation;
            setInstallationDevices(updatedInstallation.devices || []);
            setSelectedDevicesToAssociate([]);
            setDeviceActionNotes('');
            await loadTrackableDevices();
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to associate devices');
        } finally {
            setSavingDeviceAssignments(false);
        }
    };

    const handleAssociateDevices = () => {
        if (!installation || selectedDevicesToAssociate.length === 0) return;
        const hasDefective = selectedDevicesToAssociate.some((d) => d.status === 'defective');
        if (hasDefective) {
            setDefectiveAssociateConfirmOpen(true);
            return;
        }
        void runAssociateDevices();
    };

    const handleConfirmDefectiveAssociate = () => {
        setDefectiveAssociateConfirmOpen(false);
        void runAssociateDevices();
    };

    const handleDissociateDevices = async () => {
        if (!installation || selectedDevicesToDissociate.length === 0) return;

        setSavingDeviceAssignments(true);
        setError('');
        try {
            const response = await dissociateInstallationDevices(installation.uuid, {
                device_uuids: selectedDevicesToDissociate.map((d) => d.uuid),
                event_notes: deviceActionNotes || undefined,
            });

            const updatedInstallation = (response.data?.data ?? response.data) as LpsInstallation;
            setInstallationDevices(updatedInstallation.devices || []);
            setSelectedDevicesToDissociate([]);
            setDeviceActionNotes('');
            await loadTrackableDevices();
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to dissociate devices');
        } finally {
            setSavingDeviceAssignments(false);
        }
    };

    const defectiveSelectedForAssociate = selectedDevicesToAssociate.filter(
        (d) => d.status === 'defective',
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    {dialogStep === 'details' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlaceIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {isEdit ? 'Edit Installation' : 'New Installation'}
                            </Typography>
                            {isEdit && installation && (
                                <Chip
                                    label={formatStatus(installation.status)}
                                    size="small"
                                    color={STATUS_COLORS[installation.status]}
                                />
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <IconButton
                                onClick={() => setDialogStep('details')}
                                size="small"
                                aria-label="Back to installation details"
                                edge="start"
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                                Anchors & trackers
                            </Typography>
                        </Box>
                    )}
                    <IconButton onClick={onClose} size="small" aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {dialogStep === 'details' ? (
                        <>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <TextField
                                    label="Installation Name *"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    fullWidth
                                    size="small"
                                    placeholder="e.g. Spellman Performance Labs - Indoor Track"
                                />

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Autocomplete
                                        value={
                                            organizations.find(
                                                (o) => o.uuid === selectedOrg?.uuid,
                                            ) || null
                                        }
                                        onChange={(_, newValue) => setSelectedOrg(newValue)}
                                        onInputChange={(_, val, reason) => {
                                            if (reason !== 'reset') fetchOrganizations(val);
                                        }}
                                        getOptionKey={(o) => o.uuid}
                                        filterOptions={(options) => options}
                                        size="small"
                                        fullWidth
                                        options={organizations}
                                        getOptionLabel={(o) => `${o.name} - ${o.uuid.slice(0, 8)}`}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Organization" />
                                        )}
                                        sx={{ flex: 1 }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 180 }}>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={status}
                                            onChange={(e) =>
                                                setStatus(e.target.value as InstallationStatus)
                                            }
                                            label="Status"
                                        >
                                            {STATUS_OPTIONS.map((s) => (
                                                <MenuItem key={s} value={s}>
                                                    {formatStatus(s)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <TextField
                                    label="Location Address"
                                    value={locationAddress}
                                    onChange={(e) => setLocationAddress(e.target.value)}
                                    fullWidth
                                    size="small"
                                    placeholder="Street address, city, state"
                                />

                                <TextField
                                    label="Location Notes"
                                    value={locationNotes}
                                    onChange={(e) => setLocationNotes(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    size="small"
                                    placeholder="Building, floor, dimensions, anchor mounting notes..."
                                />

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Autocomplete
                                        value={selectedGateway}
                                        onChange={(_, newValue) => setSelectedGateway(newValue)}
                                        size="small"
                                        options={[
                                            ...availableGateways,
                                            ...(selectedGateway &&
                                            !availableGateways.find(
                                                (g) => g.uuid === selectedGateway.uuid,
                                            )
                                                ? [selectedGateway]
                                                : []),
                                        ]}
                                        getOptionKey={(o) => o.uuid}
                                        getOptionLabel={(o) =>
                                            `${o.serial_number}${o.model ? ` (${o.model})` : ''}`
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} label="Gateway Device" />
                                        )}
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        label="IoT Thing Name"
                                        value={iotThingName}
                                        onChange={(e) => setIotThingName(e.target.value)}
                                        size="small"
                                        sx={{ flex: 1 }}
                                        placeholder="AWS IoT thing name"
                                    />
                                    <TextField
                                        label="Device Name"
                                        value={deviceName}
                                        onChange={(e) => setDeviceName(e.target.value)}
                                        size="small"
                                        sx={{ flex: 1 }}
                                        placeholder="e.g. fiyrpod_indoor_001"
                                    />
                                    <TextField
                                        label="Installed Date"
                                        type="date"
                                        value={installedAt}
                                        onChange={(e) => setInstalledAt(e.target.value)}
                                        size="small"
                                        sx={{ flex: 1 }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Box>
                            </Box>

                            {isEdit && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 600, mb: 1.5 }}
                                    >
                                        Assigned devices
                                    </Typography>
                                    {loadingDevices ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                py: 2,
                                            }}
                                        >
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        renderAssignedAnchorsTrackersBoxes(true)
                                    )}
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                Add or remove anchors and trackers for this installation. Event
                                notes are optional and appear in custody history.
                            </Typography>
                            {loadingDevices ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                renderAssignedAnchorsTrackersBoxes(false)
                            )}

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                    Associate devices
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        alignItems: 'flex-start',
                                        mb: 1.5,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        sx={{ flex: 1, minWidth: 200 }}
                                        options={availableForAssociation}
                                        value={selectedDevicesToAssociate}
                                        onChange={(_, value) =>
                                            setSelectedDevicesToAssociate(value)
                                        }
                                        getOptionKey={(option) => option.uuid}
                                        getOptionLabel={(option) =>
                                            `${option.serial_number} (${formatStatus(
                                                option.device_type,
                                            )} • ${formatStatus(option.status)})`
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select devices to associate"
                                                placeholder="Search by serial number..."
                                            />
                                        )}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAssociateDevices}
                                        disabled={
                                            savingDeviceAssignments ||
                                            selectedDevicesToAssociate.length === 0
                                        }
                                    >
                                        {savingDeviceAssignments ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            'Associate'
                                        )}
                                    </Button>
                                </Box>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Event notes (optional)"
                                    value={deviceActionNotes}
                                    onChange={(e) => setDeviceActionNotes(e.target.value)}
                                    placeholder="Notes for custody history / audit log..."
                                />
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                    Dissociate devices
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        alignItems: 'flex-start',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        sx={{ flex: 1, minWidth: 200 }}
                                        options={assignedTrackableDevices}
                                        value={selectedDevicesToDissociate}
                                        onChange={(_, value) =>
                                            setSelectedDevicesToDissociate(value)
                                        }
                                        getOptionKey={(option) => option.uuid}
                                        getOptionLabel={(option) =>
                                            `${option.serial_number} (${formatStatus(
                                                option.device_type,
                                            )} • ${formatStatus(option.status)})`
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select devices to dissociate"
                                                placeholder="Search assigned devices..."
                                            />
                                        )}
                                    />
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        onClick={handleDissociateDevices}
                                        disabled={
                                            savingDeviceAssignments ||
                                            selectedDevicesToDissociate.length === 0
                                        }
                                    >
                                        {savingDeviceAssignments ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            'Dissociate'
                                        )}
                                    </Button>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    {dialogStep === 'details' ? (
                        <>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                                {saving ? (
                                    <CircularProgress size={20} />
                                ) : isEdit ? (
                                    'Save Changes'
                                ) : (
                                    'Create Installation'
                                )}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={() => setDialogStep('details')}>
                            Back to installation details
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={defectiveAssociateConfirmOpen}
                onClose={() => !savingDeviceAssignments && setDefectiveAssociateConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Assign defective device?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {defectiveSelectedForAssociate.length === 1
                            ? 'Are you sure you want to assign this defective device to this installation?'
                            : `Are you sure you want to assign ${defectiveSelectedForAssociate.length} defective devices to this installation?`}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDefectiveAssociateConfirmOpen(false)}
                        disabled={savingDeviceAssignments}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmDefectiveAssociate}
                        disabled={savingDeviceAssignments}
                    >
                        {savingDeviceAssignments ? (
                            <CircularProgress size={20} />
                        ) : (
                            'Yes, associate'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LpsInstallationDialog;
